import { colors } from '@ardoq/design-tokens';
import type { ArdoqIcon } from '@ardoq/data-model';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import type { LayoutBounds } from '@ardoq/graph-view';
import {
  MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE as FONT_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
} from '@ardoq/graph-labeling';
import {
  MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH as CARD_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_NODE_HEIGHT as MIN_CARD_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_SHAPE_HEIGHT as SHAPE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_PADDING as PADDING,
  MODERNIZED_BLOCK_DIAGRAM_CORNER_RADIUS as CORNER_RADIUS,
  MODERNIZED_BLOCK_DIAGRAM_NODE_BORDER_WIDTH as NODE_BORDER_WIDTH,
} from 'yfilesExtensions/styles/modernized/consts';
import {
  paintIconRepresentation,
  paintImageRepresentation,
} from 'yfilesExtensions/styles/modernized/leafNode/paintRepresentation';

/**
 * Renders a leaf node as the canonical Ardoq component card (Pei-Yu's Modernized
 * Block Diagram design) directly to canvas — a white rounded card with the
 * component-type representation (icon in a type-colored circle, or an image) above
 * a centered, wrapped label. Faithful to `LeafNodeVisual` but decoupled from the
 * block diagram's graph model: it takes a plain props object and reuses the shared
 * `canvasRendering` paint utilities + canonical constants.
 */

const CIRCLE_RADIUS = SHAPE_HEIGHT / 2;
const LABEL_FONT = `${FONT_SIZE}px ${ARDOQ_DEFAULT_FONT_FAMILY}`;

export const CARD_WIDTH_PX = CARD_WIDTH;

export type ComponentCard = {
  /** Resolved icon (from the component type's representation data). */
  icon: ArdoqIcon;
  isImage: boolean;
  /** Image URL when `isImage`, else the icon name (unused for drawing). */
  imageValue: string;
  /** Circle fill (icon) / circle outline (image) color — the resolved node color. */
  color: string;
  /** Pre-wrapped label lines (≤ 3). */
  labelLines: string[];
  /** Vertical offset of the representation circle within the card. */
  shapeY: number;
};

/**
 * Node height + representation offset for a card, derived from the wrapped label.
 * Mirrors `layoutLeafNode`: the card is at least `MIN_CARD_HEIGHT` and grows to
 * fit a multi-line label; content is centered when it fits, top-aligned when it
 * overflows.
 */
export const layoutComponentCard = (
  labelLines: string[]
): { height: number; shapeY: number } => {
  const labelHeight = labelLines.length * LINE_HEIGHT;
  const contentHeight =
    SHAPE_HEIGHT + (labelHeight ? PADDING + labelHeight : 0);
  const height = Math.max(MIN_CARD_HEIGHT, contentHeight + 2 * PADDING);
  const shapeY =
    height > MIN_CARD_HEIGHT ? PADDING : height / 2 - contentHeight / 2;
  return { height, shapeY };
};

export const drawComponentCard = (
  ctx: CanvasRenderingContext2D,
  layout: LayoutBounds,
  card: ComponentCard
): void => {
  const { x, y, width, height } = layout;
  const { color, icon, isImage, imageValue, labelLines, shapeY } = card;

  ctx.save();

  // Card chrome: white rounded rectangle with a subtle border.
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, CORNER_RADIUS);
  ctx.lineWidth = NODE_BORDER_WIDTH;
  ctx.strokeStyle = colors.grey300;
  ctx.stroke();
  ctx.fillStyle = colors.white950;
  ctx.fill();

  // Representation: image-in-circle, or icon in a type-colored circle.
  const cx = x + width / 2;
  const cy = y + shapeY + CIRCLE_RADIUS;
  if (isImage && imageValue) {
    paintImageRepresentation(ctx, imageValue, cx, cy, CIRCLE_RADIUS, color);
  } else {
    paintIconRepresentation(ctx, cx, cy, CIRCLE_RADIUS, color, icon);
  }

  // Label: centered, wrapped lines below the representation.
  ctx.font = LABEL_FONT;
  ctx.fillStyle = colors.black950;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const topLineY = y + shapeY + SHAPE_HEIGHT + PADDING + LINE_HEIGHT / 2;
  labelLines.forEach((line, lineIndex) => {
    ctx.fillText(line, cx, topLineY + LINE_HEIGHT * lineIndex);
  });

  ctx.restore();
};
